import { useFormikContext } from 'formik';
import { camelCase } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo } from 'react';

import { type InputComponent } from '..';
import { InputDate } from '../input-date';
import { Box, Stack } from '@mui/material';

export interface InputDateRangeProps extends InputComponent {
  min?: moment.Moment;
  max?: moment.Moment;
  step?: number;
}

const DATE_FORMAT = 'YYYY-MM-DD';

export function InputDateRange({
  label,
  nameOverride,
  min,
  max,
  showLabel = false,
  step = 7,
  size = 'sm',
}: InputDateRangeProps) {
  const { setFieldValue, values } =
    useFormikContext<Record<string, { start: string; end: string }>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  // The start date is never capped by the end date — moving it past the end
  // instead pushes the end out to `step` days after the new start. An empty
  // end is left alone: `InputDate` reports `''` both when the field is cleared
  // and on every keystroke of a half-typed date, so filling it in here would
  // fight the user mid-edit.
  useEffect(() => {
    const range = values[name];
    if (!range?.start || !range.end) return;

    const momentStart = moment(range.start);
    const momentEnd = moment(range.end);
    if (!momentStart.isValid() || !momentEnd.isValid()) return;
    if (!momentStart.isAfter(momentEnd)) return;

    const nextEnd = momentStart.clone().add(step, 'days');
    const nextEndValue = (max && nextEnd.isAfter(max) ? max : nextEnd).format(
      DATE_FORMAT,
    );
    // Keep the effect idempotent: when `max` sits behind the start the clamped
    // value never satisfies the guard above, so without this it would re-write
    // (and re-render) on every run.
    if (nextEndValue === range.end) return;

    setFieldValue(`${name}.end`, nextEndValue);
  }, [max, name, setFieldValue, step, values]);

  return (
    // Side by side where there is room, stacked on a phone. Two date inputs
    // in a row inside a dialog left each about 120px wide, which is narrower
    // than the date text they hold — the value was clipped and the picker
    // button sat on top of it. The dash only reads as "to" horizontally, so
    // it goes when the pair stacks.
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      sx={{ '& > *': { minWidth: 0 } }}
    >
      <InputDate
        label="Start Date"
        nameOverride={`${name}.start`}
        showLabel={showLabel}
        min={min?.format(DATE_FORMAT)}
        max={max?.format(DATE_FORMAT)}
        required
        size={size}
      />
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{'-'}</Box>
      <InputDate
        label="End Date"
        nameOverride={`${name}.end`}
        showLabel={showLabel}
        min={
          values[name].start
            ? moment(values[name].start).format(DATE_FORMAT)
            : min?.format(DATE_FORMAT)
        }
        max={max?.format(DATE_FORMAT)}
        required
        size={size}
      />
    </Stack>
  );
}
